/**
 * Auth0 connection routing for the Sign In flow.
 *
 * The Auth0 `connection` extra-param selects which Identity Provider
 * (database / passwordless / Azure AD / etc.) handles authentication for a
 * given authorization request. We pick it client-side from the email domain
 * the user enters so SSO users land in the right place without an extra
 * picker UI.
 *
 * Strings here mirror the Auth0 dashboard configuration — keep them in sync
 * with the connection names in the Vision Auth tenants.
 */

/**
 * Auth0 connection identifiers, addressed by name so they can never drift out
 * of sync with the Auth0 dashboard configuration.
 */
export const AUTH_CONNECTIONS = {
  /** Vision Intelligence Microsoft Entra (Azure AD) SSO connection. */
  microsoftVisioni: "microsoft-visioni",
  /** Auth0 passwordless-email connection — fallback for non-SSO users. */
  email: "email",
} as const;

export type AuthConnection = (typeof AUTH_CONNECTIONS)[keyof typeof AUTH_CONNECTIONS];

/**
 * Email domains routed through the Microsoft Entra (Azure AD) SSO connection.
 *
 * Stored lower-case for case-insensitive comparison. Extend this list when a
 * new organisation onboards onto the Vision Intelligence Entra tenant.
 *
 * Sub-domains are intentionally not matched (e.g. `alice@team.visioni.com.au`
 * falls back to the email connection). Add the explicit sub-domain here if it
 * needs SSO routing.
 */
export const MICROSOFT_VISIONI_EMAIL_DOMAINS: readonly string[] = [
  "visioni.com.au",
  "visioni.co.nz",
  "atfservices.com.au",
  "atfservices.co.nz",
];

/**
 * Pick the Auth0 connection to use for the given email address.
 *
 * Pure — does no I/O and does not throw on malformed input. Callers should
 * have already validated email format via the Zod sign-in schema; this
 * function falls back to the email connection for any input it can't parse.
 */
export function resolveAuthConnection(email: string): AuthConnection {
  // `lastIndexOf` tolerates quoted local parts like `"foo@bar"@visioni.com.au`.
  const at = email.lastIndexOf("@");
  if (at === -1) return AUTH_CONNECTIONS.email;
  const domain = email
    .slice(at + 1)
    .trim()
    .toLowerCase();
  return MICROSOFT_VISIONI_EMAIL_DOMAINS.includes(domain) ? AUTH_CONNECTIONS.microsoftVisioni : AUTH_CONNECTIONS.email;
}
